import React, { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { useLanguage, languages } from '../contexts/LanguageContext';

export default function LanguageSelector({ className = '', variant = 'default' }) {
  const { language, setLanguage, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLang = languages[language];

  const handleSelect = (langCode) => {
    setLanguage(langCode);
    setIsOpen(false);
  };

  if (variant === 'minimal') {
    return (
      <div className={`relative ${className}`} ref={containerRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 text-sm hover:text-gold transition-colors"
        >
          <Globe className="w-4 h-4" />
          <span>{currentLang.flag}</span>
        </button>
        
        {isOpen && (
          <div className="absolute top-full mt-2 right-0 bg-white rounded-lg shadow-xl border py-1 min-w-[140px] z-50">
            {Object.entries(languages).map(([code, lang]) => (
              <button
                key={code}
                onClick={() => handleSelect(code)}
                className={`w-full px-4 py-2 text-sm flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                  language === code ? 'text-gold font-medium' : 'text-gray-700'
                }`}
              >
                <span className="text-lg">{lang.flag}</span>
                <span className="flex-1 text-right">{lang.name}</span>
                {language === code && <Check className="w-4 h-4 text-gold" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-white border rounded-lg hover:border-gold transition-colors"
      >
        <Globe className="w-4 h-4 text-gray-500" />
        <span className="text-lg">{currentLang.flag}</span>
        <span className="text-sm font-medium">{currentLang.name}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-xl border py-2 min-w-[180px] z-50">
          <div className="px-3 py-2 border-b mb-1">
            <p className="text-xs text-gray-500">{t('common.selectLanguage')}</p>
          </div>
          {Object.entries(languages).map(([code, lang]) => (
            <button
              key={code}
              onClick={() => handleSelect(code)}
              className={`w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                language === code ? 'bg-gold/10' : ''
              }`}
            >
              <span className="text-xl">{lang.flag}</span>
              <span className={`flex-1 text-sm ${language === code ? 'font-semibold text-gold' : 'text-gray-700'}`}>
                {lang.name}
              </span>
              {language === code && (
                <Check className="w-5 h-5 text-gold" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
